// ChatRoom DO の参加者リストを管理する共通モジュール。
// 全ページ (/、/club、/lite) が同じ ChatRoom DO に WS 接続するので、
// USER_LIST / USER_JOIN / USER_LEAVE / USER_VISIBILITY message のハンドリングと
// UI render を共通化する。
//
// データモデル:
//   - server は **name 単位の集約 visible + WS 接続数 (count)** を broadcast
//   - 同名 user の multi-tab では count >= 2 になる (= 「参加者: N人」カウントを維持)
//   - visible は「同名 WS のうち 1 つでも手前なら true」(= multi-tab で片方裏でも user は visible 扱い)
//
// Message protocol:
//   USER_LIST {participants: [{name, count, visible}]}  — 新規接続者にだけ initial snapshot
//   USER_JOIN {user, visible}                            — 他者の入室 (incremental)
//   USER_LEAVE {user}                                    — 他者の退室 (incremental)
//   USER_VISIBILITY {user, visible}                      — name 単位の集約 visible 変更

export interface ParticipantSnapshot {
  name: string;
  count: number;
  visible: boolean;
}

interface ParticipantsState extends ParticipantSnapshot {
  /** display order を安定させるための joined order seq (= 古い順上)。
   *  ただし render では visible / away で 2 群に分けるのでこの seq は群内 stable sort 用。 */
  seq: number;
}

export class ParticipantsList {
  private byName = new Map<string, ParticipantsState>();
  private nextSeq = 0;
  private readonly container: HTMLElement;
  private readonly countEl: HTMLElement | null;

  constructor(container: HTMLElement, countEl: HTMLElement | null = null) {
    this.container = container;
    this.countEl = countEl;
  }

  /** USER_LIST 受信時に full snapshot で置き換え (= 接続直後 1 度きり)。 */
  setAll(list: ParticipantSnapshot[]): void {
    this.byName.clear();
    for (const p of list) {
      this.byName.set(p.name, { ...p, seq: this.nextSeq++ });
    }
    this.render();
  }

  /** USER_JOIN: 既存 entry があれば count++、なければ新規追加。 */
  add(name: string, visible: boolean): void {
    const existing = this.byName.get(name);
    if (existing) {
      existing.count += 1;
      // 入ってきた WS の visible 状態で集約を上書き (= server から「集約結果」が来てる)
      existing.visible = visible;
    } else {
      this.byName.set(name, { name, count: 1, visible, seq: this.nextSeq++ });
    }
    this.render();
  }

  /** USER_LEAVE: count を 1 減らす。0 になったら entry を削除。 */
  remove(name: string): void {
    const existing = this.byName.get(name);
    if (!existing) return;
    existing.count -= 1;
    if (existing.count <= 0) {
      this.byName.delete(name);
    }
    this.render();
  }

  /** USER_VISIBILITY: name の集約 visible を更新。 */
  updateVisibility(name: string, visible: boolean): void {
    const existing = this.byName.get(name);
    if (!existing) return;
    if (existing.visible === visible) return;
    existing.visible = visible;
    this.render();
  }

  /** server からの WS message を内部で dispatch。型 unknown を runtime check で
   *  振り分ける。caller (= 各ページの handleServerMessage) はこの 1 メソッドを
   *  呼ぶだけで済む設計。 */
  handleMessage(msg: unknown): boolean {
    if (!msg || typeof msg !== "object") return false;
    const m = msg as {
      type?: unknown;
      participants?: unknown;
      user?: unknown;
      visible?: unknown;
    };
    if (m.type === "USER_LIST" && Array.isArray(m.participants)) {
      const list: ParticipantSnapshot[] = [];
      for (const item of m.participants) {
        if (!item || typeof item !== "object") continue;
        const entry = item as { name?: unknown; count?: unknown; visible?: unknown };
        if (typeof entry.name !== "string") continue;
        const count = typeof entry.count === "number" && entry.count > 0
          ? Math.floor(entry.count)
          : 1;
        list.push({ name: entry.name, count, visible: entry.visible !== false });
      }
      this.setAll(list);
      return true;
    }
    if (m.type === "USER_JOIN" && typeof m.user === "string") {
      this.add(m.user, m.visible !== false);
      return true;
    }
    if (m.type === "USER_LEAVE" && typeof m.user === "string") {
      this.remove(m.user);
      return true;
    }
    if (m.type === "USER_VISIBILITY" && typeof m.user === "string") {
      this.updateVisibility(m.user, m.visible === true);
      return true;
    }
    return false;
  }

  private render(): void {
    const entries = Array.from(this.byName.values());
    if (this.countEl) {
      // 総 WS 接続数 (= multi-tab を含む実 connection 数、既存 "参加者: N人" SYSTEM 表記と一貫)
      let total = 0;
      for (const e of entries) total += e.count;
      this.countEl.textContent = String(total);
    }
    // visible 上 / away 下にソート (= 群内は join seq 順で stable)
    entries.sort((a, b) => {
      if (a.visible !== b.visible) return a.visible ? -1 : 1;
      return a.seq - b.seq;
    });
    this.container.replaceChildren();
    for (const p of entries) {
      const li = document.createElement("li");
      li.className = p.visible ? "participant participant--visible" : "participant participant--away";
      const dot = document.createElement("span");
      dot.className = "participant__dot";
      dot.textContent = p.visible ? "●" : "○";
      const nameEl = document.createElement("span");
      nameEl.className = "participant__name";
      // multi-tab で count > 1 の時は "Alice ×2" 表示。textContent で escape (= XSS 不可)
      nameEl.textContent = p.count > 1 ? `${p.name} ×${p.count}` : p.name;
      li.appendChild(dot);
      li.appendChild(nameEl);
      if (!p.visible) {
        const status = document.createElement("span");
        status.className = "participant__status";
        status.textContent = " (退室中)";
        li.appendChild(status);
      }
      this.container.appendChild(li);
    }
  }
}
